/**
 * A football field drawn left to right from the away end zone to the home end
 * zone, with a marker where the ball is (ISSUE-1091). The end zones are a
 * fixed width rather than to scale, so a four or five letter abbreviation
 * fits. The server sends the marker's position as a percent of the field of
 * play; this component only clamps and draws it. Theme tokens only, never a
 * team color. Decorative: the server's sentence names the ball spot.
 *
 * ISSUE-1383, v1 `football_gamecast_view.dart`: when the server knows them,
 * the drive so far is an accent bar from where the drive started to the ball,
 * and the line to gain is a warning colored line.
 *
 * Inline `style` carries only `left` and `width` percentages: the yard lines,
 * the yard numbers, the marker, the drive bar and the first down line are
 * positioned along the field, which no utility class expresses.
 */
import type { ReactNode } from "react";

export type typeFieldStripProps = {
  awayLabel: string;
  homeLabel: string;
  ballSpotPercentFromAway: number;
  /** ISSUE-1383. Where the current drive started; null or absent when unknown. */
  driveStartPercentFromAway?: number | null | undefined;
  /** ISSUE-1383. The line to gain; null or absent when unknown or the goal line. */
  firstDownPercentFromAway?: number | null | undefined;
};

const YARD_LINE_PERCENTS = [10, 20, 30, 40, 50, 60, 70, 80, 90] as const;
const YARD_LABELS = [
  { percent: 20, label: "20" },
  { percent: 50, label: "50" },
  { percent: 80, label: "20" },
] as const;

const END_ZONE_CLASS_NAME =
  "flex w-12 shrink-0 items-center justify-center bg-ink-secondary px-1 text-center text-small font-bold text-surface break-words";

const clampPercent = (value: number): number =>
  Number.isFinite(value) ? Math.min(100, Math.max(0, value)) : 50;

const optionalPercent = (value: number | null | undefined): number | null =>
  value === null || value === undefined || !Number.isFinite(value)
    ? null
    : clampPercent(value);

export const FieldStrip = (props: typeFieldStripProps): ReactNode => {
  const percent = clampPercent(props.ballSpotPercentFromAway);
  const driveStart = optionalPercent(props.driveStartPercentFromAway);
  const firstDown = optionalPercent(props.firstDownPercentFromAway);
  return (
    <div
      className="flex flex-col gap-1"
      data-testid="field-strip"
      aria-hidden="true"
    >
      <div className="flex h-12 w-full overflow-hidden rounded-md border border-hairline">
        <span
          className={END_ZONE_CLASS_NAME}
          data-testid="field-strip-endzone-away"
        >
          {props.awayLabel}
        </span>
        <div className="relative flex-1 bg-surface-sunken">
          {YARD_LINE_PERCENTS.map((left) => (
            <span
              key={left}
              className="absolute inset-y-0 w-px bg-hairline"
              style={{ left: `${left}%` }}
            />
          ))}
          {driveStart !== null && driveStart !== percent ? (
            <span
              data-testid="field-strip-drive"
              data-from={String(driveStart)}
              className="absolute top-1/2 h-2 -translate-y-1/2 rounded-full bg-accent"
              style={{
                left: `${Math.min(driveStart, percent)}%`,
                width: `${Math.abs(percent - driveStart)}%`,
              }}
            />
          ) : null}
          {firstDown !== null ? (
            <span
              data-testid="field-strip-first-down"
              data-percent={String(firstDown)}
              className="absolute inset-y-0 w-0.5 -translate-x-1/2 bg-warning-solid"
              style={{ left: `${firstDown}%` }}
            />
          ) : null}
          <span data-testid="field-strip-marker" data-percent={String(percent)}>
            <span
              className="absolute inset-y-0 w-0.5 -translate-x-1/2 bg-primary"
              style={{ left: `${percent}%` }}
            />
            <span
              className="absolute top-1 h-3 w-3 -translate-x-1/2 rounded-full bg-primary"
              style={{ left: `${percent}%` }}
            />
          </span>
        </div>
        <span
          className={END_ZONE_CLASS_NAME}
          data-testid="field-strip-endzone-home"
        >
          {props.homeLabel}
        </span>
      </div>
      <div className="flex">
        <span className="w-12 shrink-0" />
        <div className="relative h-4 flex-1">
          {YARD_LABELS.map((yard) => (
            <span
              key={yard.percent}
              data-testid="field-strip-yard-label"
              className="absolute -translate-x-1/2 text-small text-ink-tertiary tabular-nums"
              style={{ left: `${yard.percent}%` }}
            >
              {yard.label}
            </span>
          ))}
        </div>
        <span className="w-12 shrink-0" />
      </div>
    </div>
  );
};
